/* PDP v3 age-band size finder.
 * Ported from agents/reference/pdp-redesign-v3.html (Stage 2 of
 * agents/plans/2026-07-06-pdp-v3-size-finder-section-plan.md).
 *
 * Head circumference is roughly normal for a given age, so each age band's
 * "most likely size" and its neighbours are read off the CDF rather than a
 * single lookup. Anchors map to WHO/CDC head-circumference-for-age norms;
 * shares are AVERAGED across each bracket so the % describes the whole range.
 *
 * DATA FLOW: Airtable "📦 Products" size_finder_json ➜ (sync) ➜ product metafield
 * custom.size_finder ➜ section renders it into [data-sf-data] ➜ parsed here.
 * The row contract and its label/unit/measure derivation live in the SHARED
 * data layer, assets/toshi-size-finder-data.js (window.ToshiSizeFinderData),
 * also consumed by toshi-size-finder-chart-page.js — change the rules there
 * and both finders follow. The section must load toshi-size-finder-data.js
 * (defer) BEFORE this file.
 *
 * Sizes/bands come from a JSON script tag rendered by the section
 * ([data-sf-data]) — falls back to a hard-coded hat seed in Liquid when the
 * product has no custom.size_finder metafield.
 * Availability comes from the real variant-picker radios, and selecting a
 * size checks the matching radio + dispatches 'change' so the theme's own
 * variant flow (hidden id input, price, ATC state, URL) does the rest.
 */
(() => {
  const MEAN_ANCHORS = [
    [0, 34.2], [1, 36.7], [2, 38.5], [3, 40.0], [4, 41.1], [6, 42.9], [9, 44.5],
    [12, 45.5], [18, 46.9], [24, 47.75], [36, 49.0], [48, 49.8], [60, 50.3],
    [72, 50.9], [84, 51.4], [96, 51.9], [108, 52.3], [120, 52.7], [144, 53.5],
    [168, 54.2], [192, 54.8], [216, 55.2],
  ];
  const SD_ANCHORS = [
    [0, 1.25], [3, 1.20], [12, 1.35], [24, 1.40], [36, 1.45], [60, 1.55],
    [120, 1.65], [144, 1.70], [216, 1.80],
  ];

  function interp(anchors, x) {
    if (x <= anchors[0][0]) return anchors[0][1];
    const last = anchors[anchors.length - 1];
    if (x >= last[0]) return last[1];
    for (let i = 1; i < anchors.length; i += 1) {
      if (x <= anchors[i][0]) {
        const [x0, y0] = anchors[i - 1];
        const [x1, y1] = anchors[i];
        return y0 + (y1 - y0) * ((x - x0) / (x1 - x0));
      }
    }
    return last[1];
  }

  function erf(x) {
    const t = 1 / (1 + 0.3275911 * Math.abs(x));
    const y = 1 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t
      - 0.284496736) * t + 0.254829592) * t * Math.exp(-x * x);
    return x >= 0 ? y : -y;
  }

  const normCdf = (x, mean, sd) => 0.5 * (1 + erf((x - mean) / (sd * Math.SQRT2)));
  // shared data layer (assets/toshi-size-finder-data.js) — row parsing,
  // label/unit rules, measure splitting, and the size-chart table build +
  // row-highlight live there, not here (shared with the size-chart page finder)
  const {
    norm, parseRows, intersectRows, splitMeasure, buildChartTable, markChartRow,
    createSizeSelection, cardInnerHtml, emptyCardInnerHtml, selectTab, isDevSurface,
  } = window.ToshiSizeFinderData;

  /* Airtable rollup rows → internal {bands, sizes} shape.
   *
   * The custom.size_finder metafield may be a flat array of per-size rows
   * generated from the Airtable Sizes table (see the row contract in
   * toshi-size-finder-data.js). A row WITH an age band is both a size and its
   * age chip, so the recommendation is direct — no head-circumference
   * statistics, which also keeps height-based Clothing rows honest. A
   * CHIPLESS row (blank age_low — e.g. headwear XL, a larger-head fit variant
   * of 13+) is a size with no chip of its own: it appears only as a
   * neighbouring alternate card and a chart-table row, so bands and sizes are
   * no longer strictly 1:1 — each band carries `sizeIdx`, its size's index.
   *
   * The shared parseRows() owns the display derivation (labels, units,
   * measures, series order); this wrapper adds what only the PDP model needs
   * — internal band edges continuous in months: a label gap (5y → 6y, 13
   * after 12) is bridged at the midpoint, a point age widens to the midpoints
   * of its neighbours, and the open end is capped for the model.
   */
  function normalizeRows(rows, sizeDisplay) {
    const parsed = parseRows(rows, sizeDisplay);
    if (!parsed.length) return {};
    // chips come only from rows with an age band; a chart of ONLY chipless
    // rows yields no bands, so init() bails and the raw size pills stay
    const banded = parsed
      .map((p, sizeIdx) => ({ p, sizeIdx }))
      .filter(({ p }) => p.hasBand);
    if (!banded.length) return {};

    // continuous [edge[i], edge[i+1]) ladder in months (banded rows only —
    // a chipless row has no ages to ladder)
    const edges = banded.map(({ p }, i) => {
      if (i === 0) return p.loMonths === p.hiMonths ? 0 : p.loMonths;
      const prevHi = banded[i - 1].p.hiMonths == null ? p.loMonths : banded[i - 1].p.hiMonths;
      return (prevHi + p.loMonths) / 2;
    });
    const last = banded[banded.length - 1].p;
    const lastEdge = edges[edges.length - 1];
    const end = last.hiMonths == null
      ? Math.max(240, lastEdge + 12)
      : (last.loMonths === last.hiMonths ? last.hiMonths + Math.max(1, last.hiMonths - lastEdge) : last.hiMonths);

    return {
      category: norm(rows[0].size_finder_name || ''),
      option_name: 'Size',
      direct: true, // band → its own size — skip the head-circ statistical model
      bands: banded.map(({ p, sizeIdx }, i) => ({
        label: p.label,
        unit: p.unit,
        lo: edges[i],
        hi: i + 1 < edges.length ? edges[i + 1] : end,
        sizeIdx, // index into sizes — bands can skip chipless sizes
      })),
      sizes: parsed.map((p) => {
        const nums = String(p.measure).match(/\d+(?:\.\d+)?/g) || [];
        return {
          size: p.size,
          option: p.name,
          lo: nums.length ? Number(nums[0]) : null,
          hi: nums.length > 1 ? Number(nums[1]) : (nums.length ? Number(nums[0]) : null),
          cm: p.measure,
          secondary: p.secondary,
          aliases: [],
        };
      }),
    };
  }

  class SizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      try {
        this.init();
      } finally {
        // The section pre-hides the raw Size pills from first paint (html.js
        // rule) so they can't flash before this deferred script runs. That
        // must FAIL OPEN: whenever the finder did NOT take over the radios
        // (no/bad chart data, zero matching rows, single-variant hide, no
        // listed Size selector, or a thrown error), mark the form .sf-nobind
        // so the standard size UI comes back.
        const form = this.closest('product-form');
        if (form && !form.classList.contains('sf-ready')) form.classList.add('sf-nobind');
      }
    }

    init() {
      const dataEl = this.querySelector('[data-sf-data]');
      if (!dataEl) return;
      let data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      /* No size_chart metaobject on this product (data-sf-fallback): never
         take over with the hat seed — a clothing product would get headwear
         ages and its shoppers a wrong (or dead) picker. Stand down exactly
         like the zero-match case: sf-ready is never added, so the finally
         block's .sf-nobind reveals the theme's standard size pills and the
         purchase path stays intact. Shoppers see no finder at all; dev
         surfaces keep a loud warning so the missing link gets fixed. */
      if (this.dataset.sfFallback != null) {
        this.applyNoDataFallback();
        return;
      }
      /* size_chart metaobject payload (the section emits the referenced
         chart's FULL row superset plus this product's real size-option
         values): intersect first — exactly, on size_name — so age chips,
         cards and table are built from rows backed by a real variant only.
         Superset rows are dropped here (the Page renders them all); option
         values with no chart row surface as a dev warning, never a fuzzy
         match. Inventory is not consulted — sold-out sizes stay members. */
      if (data && Array.isArray(data.chart_rows)) {
        const cut = intersectRows(data.chart_rows, data.option_values || []);
        this.chartName = data.chart_name || '';
        this.unmatchedValues = cut.unmatchedValues;
        this.duplicateCodes = cut.duplicateCodes;
        if (!cut.rows.length) {
          // zero matching rows: never invent recommendations or hide the real
          // variant picker (sf-ready is never added). Dev surfaces get a loud
          // warning naming the chart and the option values; shoppers get no
          // finder at all.
          this.applyZeroMatchWarning(data.option_values || []);
          return;
        }
        const normalized = normalizeRows(cut.rows, data.size_display);
        if (data.category) normalized.category = norm(data.category);
        data = normalized;
      } else if (Array.isArray(data)) {
        // flat Airtable rollup rows (legacy shape) → internal shape
        data = normalizeRows(data);
      }
      this.direct = !!data.direct;
      // normalize the metafield's typed keys (lo_cm/hi_cm, lo_months/hi_months)
      // and derive display strings from the numbers
      this.sizes = (data.sizes || []).map((s) => {
        const lo = s.lo_cm != null ? s.lo_cm : s.lo;
        const hi = s.hi_cm != null ? s.hi_cm : s.hi;
        return { ...s, lo, hi, cm: s.cm || `${lo}–${hi} cm` };
      });
      this.bands = (data.bands || []).map((b) => ({
        ...b,
        lo: b.lo_months != null ? b.lo_months : b.lo,
        hi: b.hi_months != null ? b.hi_months : b.hi,
      }));
      this.optionName = data.option_name || 'Size';
      this.soldOutText = this.dataset.soldoutText || 'Sold out';
      this.category = data.category || ''; // e.g. "headwear" / "clothing" (from size_finder_name)
      if (!this.sizes.length || !this.bands.length) return;

      this.sfAges = this.querySelector('[data-sf-ages]');
      this.sfSizes = this.querySelector('[data-sf-sizes]');
      this.sfHowRow = this.querySelector('[data-sf-howrow]');
      this.tipBtn = this.querySelector('[data-sf-tip-btn]');
      // the card the trigger controls (was resolved via the now-removed X's
      // parent; target it directly via the trigger's aria-controls id)
      this.tipPop = document.getElementById(this.tipBtn?.getAttribute('aria-controls') || '')
        || this.querySelector('.sf-tipinline');
      this.productForm = this.closest('product-form');

      // A product with one (or zero) variant has no size to choose and nothing
      // to measure — hide the whole finder, no warning. This keys on the REAL
      // product variant count (data-variant-count), not the data row count: a
      // product with NO metafield falls back to the 6-size hat seed, so the
      // seed count would wrongly look multi-size. The variant count is the
      // honest signal for "is this actually a sized product". Single-size also
      // covers a genuine one-row metafield (count matches).
      const variantCount = parseInt(this.dataset.variantCount, 10);
      if ((Number.isFinite(variantCount) && variantCount <= 1) || this.sizes.length === 1) {
        this.hidden = true;
        return;
      }

      // label carries the category ("Headwear Size Finder"); the base label
      // ("Size Finder") comes from the block setting via data-base-label
      this.applyLabel();
      // dev-only: a MULTI-variant product with no metafield fell back to the
      // hardcoded hat seed — a real mis-assignment worth catching pre-launch.
      // (Single-variant no-data products are already hidden above, silently.)
      this.applyDevWarning();
      // dev-only notice in the modal where a missing size-chart image would sit
      this.applyMissingImageWarning();

      /* The youngest age is pre-selected on load so the ATC is never dead, but
         the shopper hasn't ENGAGED yet: until they click an age pill, the size
         cards / summary / tip stay hidden and the pre-picked pill shows in cream
         (a soft default), not the full taupe of an active pick. `engaged` flips
         true on the first pill click and drives both the reveal and cream→taupe. */
      this.bandI = 0; // default to the youngest age (first band); never null
      this.engaged = false; // false = cards collapsed (cream pill); true = shown (taupe)
      // PDP deselection returns to an unticked recommendation; the reference
      // chart configures the shared model to re-tick its hero instead.
      this.selection = createSizeSelection({ retainConfirmationOnDeselect: false });

      this.valueMap = this.buildValueMap();
      this.hydrateAgeChips();
      this.bindKeyboard();
      this.bindTip();
      this.buildChartTable(); // fill the size-chart modal once from the parsed data

      // shopper may also drive the raw variant picker — keep the finder honest
      this.productForm?.addEventListener('on:variant:change', () => this.syncFromVariant());

      // the theme's variant flow replaces the ATC button (dynamic-variant-content)
      // AFTER on:variant:change, so re-stamp the size on the label whenever the
      // form subtree changes; applyAtcLabel is write-if-different to avoid loops
      if (this.productForm) {
        new MutationObserver(() => this.applyAtcLabel()).observe(this.productForm, { childList: true, subtree: true });
      }

      // signal the section CSS that the finder can really drive the size
      // radios — the raw pills are only hidden once this class lands, so a
      // JS failure / mis-shaped metafield / dropdown-style selector leaves
      // the standard size UI usable
      if (this.productForm && this.sizeSelector()) {
        this.productForm.classList.add('sf-ready');
      }

      // captured once: the theme's update_url strips ?view on the first
      // variant change, so location.search can't be trusted later
      this.viewParam = new URLSearchParams(window.location.search).get('view');

      // a swatch click stashes the scroll position so the sibling page can
      // resume at the same spot (siblings share the template, so offsets match)
      this.productForm?.addEventListener('click', (e) => {
        if (e.target.closest('.related-swatches a')) {
          try {
            sessionStorage.setItem('sf-swatch-scroll', JSON.stringify({ y: window.scrollY, t: Date.now() }));
          } catch (err) { /* storage blocked — colour switch just lands at the top */ }
        }
      });

      // restoreFromUrl renders if it restored a colourway-carried selection;
      // otherwise seed from whatever variant the page already loaded with
      // (?variant= link, or the theme's own first-available-variant pick),
      // falling back to the initial state (default youngest age, cream chip,
      // pre-filled ATC, cards still hidden until the shopper engages)
      if (!this.restoreFromUrl() && !this.seedFromCheckedVariant()) this.renderFinder();
      this.restoreScroll();
    }

    /* ---- colourway carry-over ------------------------------------------ */

    /* Colourways are sibling PRODUCTS (related-swatches links), so switching
       colour is a full navigation and finder state dies with the page. The
       state rides ?sf_band/?sf_size on the swatch links instead: restored
       here on the next page, matched against THAT product's own bands/sizes —
       an unknown band or size just falls back to the normal flow. */
    restoreFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const bandLabel = params.get('sf_band');
      if (bandLabel === null) return false;
      const bi = this.bands.findIndex((b) => norm(b.label) === norm(bandLabel));
      if (bi < 0) return false;
      this.bandI = bi;
      this.engaged = true; // a band carried across a colourway switch = already engaged
      const sizeCode = params.get('sf_size');
      if (sizeCode !== null) {
        const si = this.sizes.findIndex((s) => norm(s.size) === norm(sizeCode));
        if (si >= 0) {
          this.selection.setExplicit(si); // a carried size was already chosen
        }
      }
      this.renderFinder();
      return true;
    }

    /* ---- initial-load variant carry-over -------------------------------- */

    /* The product form can load with a real variant already checked via an
       explicit ?variant= link — Liquid's selected_or_first_available_variant
       resolves it into the radios server-side, with no 'on:variant:change'
       event to catch it (that only fires on a user's radio click). Without
       this, the finder shows the right band and "Most common" card but never
       ticks it, because `confirmed` stays false until the shopper clicks.
       Gated on the URL param (not "is some variant checked", which is true on
       every load via Shopify's own first-available-variant default) so an
       ordinary product visit still starts collapsed as designed. */
    seedFromCheckedVariant() {
      if (!new URLSearchParams(window.location.search).has('variant')) return false;
      const selector = this.sizeSelector();
      if (!selector) return false;
      const checked = selector.querySelector('input.js-option:checked');
      if (!checked) return false;
      const idx = this.valueMap.get(norm(checked.value));
      if (idx == null) return false;
      const winnerFor = (bi) => {
        const band = this.bands[bi];
        const split = this.direct
          ? (() => { const prev = this.bandI; this.bandI = bi; const s = this.directSplit(); this.bandI = prev; return s; })()
          : this.bracketSplit(band.lo, band.hi);
        return split.indexOf(Math.max(...split));
      };
      let bi = this.bands.findIndex((b, i) => winnerFor(i) === idx);
      // a CHIPLESS size (headwear XL) is never any band's winner — seed the
      // band it neighbours instead, so the ✓ lands on the right card window
      if (bi < 0) {
        bi = this.bands.findIndex((b, i) => Math.abs(winnerFor(i) - idx) === 1);
      }
      this.bandI = bi >= 0 ? bi : 0;
      // NOT this.engaged = true: the cards must stay collapsed until the
      // shopper clicks an age pill, even when a size is already pre-picked.
      // confirmed just means the (still-hidden) card is pre-ticked for when
      // they do expand it.
      this.selection.setExplicit(idx);
      this.renderFinder();
      return true;
    }

    /* resume the previous page's scroll position after a swatch navigation —
       consumed on read, and ignored when stale (>30s) so an old stash can't
       hijack a fresh visit that arrives via back/forward or a shared link */
    restoreScroll() {
      let stash;
      try {
        stash = JSON.parse(sessionStorage.getItem('sf-swatch-scroll'));
        sessionStorage.removeItem('sf-swatch-scroll');
      } catch (err) {
        return;
      }
      if (!stash || typeof stash.y !== 'number' || Date.now() - stash.t > 30000) return;
      // apply immediately (rAF alone can be deferred until the tab paints),
      // then re-assert on the first frame in case layout settles under us
      const apply = () => window.scrollTo({ top: stash.y, behavior: 'instant' });
      apply();
      requestAnimationFrame(apply);
    }

    updateSwatchLinks() {
      if (!this.productForm) return;
      this.productForm.querySelectorAll('.related-swatches a[href]').forEach((a) => {
        const url = new URL(a.getAttribute('href'), window.location.origin);
        if (this.bandI !== null) {
          url.searchParams.set('sf_band', this.bands[this.bandI].label);
          const idx = this.selection.currentIdx();
          if (idx != null) url.searchParams.set('sf_size', this.sizes[idx].size);
          else url.searchParams.delete('sf_size');
          if (this.viewParam) url.searchParams.set('view', this.viewParam);
        } else {
          url.searchParams.delete('sf_band');
          url.searchParams.delete('sf_size');
        }
        a.setAttribute('href', url.pathname + url.search);
      });
    }

    /* ---- variant-picker bridge ---------------------------------------- */

    sizeSelector() {
      if (!this.productForm) return null;
      const selectors = this.productForm.querySelectorAll('.option-selector[data-selector-type="listed"]');
      return [...selectors].find((s) => norm(s.dataset.option) === norm(this.optionName)) || null;
    }

    /* Single source of truth for value↔size matching (used by radioFor and
     * syncFromVariant): normalized radio value → size index, built once at
     * init against the REAL radio values in the DOM. Claims are tiered —
     * exact option value beats size code beats alias — and a value two sizes
     * claim at the same tier binds to neither. A renamed option value that
     * no longer matches its size therefore renders it unbuyable (struck)
     * instead of silently adding a neighbouring variant. */
    buildValueMap() {
      const map = new Map();
      const selector = this.sizeSelector();
      if (!selector) return map;
      const claims = new Map(); // norm(value) -> best {tier, idx, ambiguous}
      this.sizes.forEach((s, idx) => {
        const tiers = [[s.option], [s.size], s.aliases || []];
        tiers.forEach((values, tier) => {
          values.filter(Boolean).forEach((v) => {
            const key = norm(v);
            const prev = claims.get(key);
            if (!prev || tier < prev.tier) {
              claims.set(key, { tier, idx, ambiguous: false });
            } else if (tier === prev.tier && prev.idx !== idx) {
              prev.ambiguous = true;
            }
          });
        });
      });
      [...selector.querySelectorAll('input.js-option')].forEach((r) => {
        const claim = claims.get(norm(r.value));
        if (claim && !claim.ambiguous) map.set(norm(r.value), claim.idx);
      });
      return map;
    }

    radioFor(i) {
      const selector = this.sizeSelector();
      if (!selector) return null;
      return [...selector.querySelectorAll('input.js-option')]
        .find((r) => this.valueMap.get(norm(r.value)) === i) || null;
    }

    inStock(i) {
      // no listed selector at all (single-variant product / dropdown style):
      // degrade to advice-only rather than striking every size
      if (!this.sizeSelector()) return true;
      const radio = this.radioFor(i);
      return !!radio && !radio.classList.contains('is-unavailable');
    }

    selectVariant(i) {
      const radio = this.radioFor(i);
      if (!radio || radio.checked) return;
      radio.checked = true;
      // noFocus: the theme's variant flow otherwise focuses the (hidden) size
      // radio after its fetch, yanking the scroll position — noticeable on
      // colourway navigation, where restoreScroll has just resumed the spot
      radio.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { noFocus: true } }));
    }

    syncFromVariant() {
      const selector = this.sizeSelector();
      if (!selector || this.bandI === null) return;
      // an explicit pick of a size with no variant can't be reflected in the
      // radios — don't let the stale checked radio steal the selection back
      if (this.selection.explicitIdx != null && !this.radioFor(this.selection.explicitIdx)) return;
      const checked = selector.querySelector('input.js-option:checked');
      if (!checked) return;
      const mapped = this.valueMap.get(norm(checked.value));
      const i = mapped == null ? -1 : mapped;
      if (i < 0) return;
      const target = this.selection.currentIdx();
      if (i !== target) {
        this.selection.setExplicit(i); // choosing via the raw variant picker is explicit too
        this.engaged = true; // …and reveals the finder if it wasn't already
        this.renderFinder();
      }
    }

    /* ---- model --------------------------------------------------------- */

    ageSplit(m) {
      const mean = interp(MEAN_ANCHORS, m);
      const sd = interp(SD_ANCHORS, m);
      const raw = this.sizes.map((s, i) => {
        const lo = i === 0 ? 30 : s.lo;
        const hi = i === this.sizes.length - 1 ? 64 : s.hi;
        return Math.max(0, normCdf(hi, mean, sd) - normCdf(lo, mean, sd));
      });
      const sum = raw.reduce((a, b) => a + b, 0) || 1;
      return raw.map((r) => r / sum);
    }

    /* direct mode (flat rollup rows): the band's own size (sizeIdx — bands
       can skip chipless sizes; legacy shapes without it are still 1:1, so
       fall back to the band index) is the exact "most common" pick.
       Neighbour weights only matter when the winner is sold out — size up
       outranks size down, matching the brand's "size up to allow for growth"
       guidance. A chipless neighbour (headwear XL) gets its card slot this
       way without ever being the recommendation. */
    directSplit() {
      const split = this.sizes.map(() => 0);
      const band = this.bands[this.bandI];
      const i = band && band.sizeIdx != null ? band.sizeIdx : this.bandI;
      split[i] = 1;
      if (i + 1 < split.length) split[i + 1] = 0.3;
      if (i - 1 >= 0) split[i - 1] = 0.25;
      return split;
    }

    bracketSplit(lo, hi) {
      const N = 24;
      const acc = this.sizes.map(() => 0);
      for (let j = 0; j <= N; j += 1) {
        const sp = this.ageSplit(lo + ((hi - lo) * j) / N);
        sp.forEach((p, i) => { acc[i] += p; });
      }
      return acc.map((a) => a / (N + 1));
    }

    /* ---- rendering ------------------------------------------------------ */

    /* Prefix the base label with the category from the data, so the heading
       reads "Headwear Size Finder" / "Clothing Size Finder". The base label
       ("Size Finder") comes from the block setting (data-base-label); with no
       category we leave it untouched. Title-cases the category token. */
    applyLabel() {
      const el = this.querySelector('[data-sf-label]');
      if (!el) return;
      const base = this.dataset.baseLabel || el.textContent.trim() || 'Size Finder';
      if (!this.category) { el.textContent = base; return; }
      const cat = this.category.charAt(0).toUpperCase() + this.category.slice(1);
      el.textContent = `${cat} ${base}`;
    }

    /* Dev-only guard: the section falls back to a hardcoded HAT seed when a
       product has no custom.size_finder metafield. That's invisible in prod
       and easy to ship by accident, so on non-production surfaces (theme
       preview / *.myshopify.com / theme editor) we surface a loud inline
       warning under the label. Real storefront visitors never see it. */
    /* the SHARED dev-surface gate (toshi-size-finder-data.js), used by
       applyDevWarning, applyZeroMatchWarning and applyMissingImageWarning. */
    isDevSurface() {
      return isDevSurface();
    }

    /* The modal's size-chart image comes from the custom.size_finder_modal_image
       metafield (Airtable ➜ sync ➜ metafield). When it's empty the modal renders
       text-only, which looks intentional — so on dev surfaces only, show a notice
       where the image would have been. Shoppers never see it; a live product with
       an unsynced image just gets the clean text-only modal. */
    applyMissingImageWarning() {
      const el = this.querySelector('[data-sf-noimg]');
      if (!el) return;
      el.hidden = !this.isDevSurface();
    }

    applyDevWarning() {
      const el = this.querySelector('[data-sf-devwarn]');
      if (!el) return;
      // show only on a dev surface, for any of: the product fell back to the
      // hat seed entirely (no chart reference), chart-level fields are still
      // unpopulated in Airtable (data-sf-missing lists them), some variant
      // size values matched no chart row, or the chart itself is ambiguous
      // (duplicate size_name). Otherwise keep it hidden (also clears any
      // stale state, e.g. a cloned node that was showing it).
      const lines = [];
      // one actionable line per gap (see the section's sf_missing assign);
      // the no-metafield case never reaches here — init() stands down early
      // via applyNoDataFallback().
      if (this.dataset.sfMissing) lines.push(...this.dataset.sfMissing.split(' | '));
      if (this.unmatchedValues?.length) {
        lines.push(`Unmatched variant size value(s) — no "${this.chartName || 'size chart'}" row: ${this.unmatchedValues.join(', ')}`);
      }
      if (this.duplicateCodes?.length) {
        lines.push(`Ambiguous chart "${this.chartName || 'size chart'}": duplicate size_name ${this.duplicateCodes.join(', ')}`);
      }
      if (lines.length && this.isDevSurface()) {
        el.textContent = lines.join('\n');
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    }

    /* A referenced chart whose rows match NONE of this product's size-option
       values is a data error, not a rendering choice: never invent
       recommendations and never hide the theme's own variant picker
       (sf-ready is not added, so the raw size pills stay). Shoppers see no
       finder at all; dev surfaces get a warning naming the chart and the
       option values so the mismatch can't be silently accepted. */
    /* No size_chart metaobject linked to this product at all: the section
       rendered the legacy hat-seed skeleton, but building a headwear finder
       on (say) a onesie would mislead shoppers — so don't. Shoppers get the
       theme's standard size pills back (via .sf-nobind, since sf-ready is
       never added) with the finder hidden entirely; dev surfaces keep the
       warning visible (with the hat chip skeleton suppressed) so the missing
       metafield link is caught before launch. */
    applyNoDataFallback() {
      if (!this.isDevSurface()) {
        this.hidden = true;
        return;
      }
      // lift the section's first-paint pre-hide (it excludes [data-sf-devmode])
      // so the warning below is actually visible on dev surfaces
      this.setAttribute('data-sf-devmode', '');
      const ages = this.querySelector('[data-sf-ages]');
      if (ages) ages.hidden = true; // never show the hat-seed age chips
      const el = this.querySelector('[data-sf-devwarn]');
      if (!el) return;
      el.textContent = 'NO SIZE CHART DATA — product has no custom.size_chart_content metafield; standard size pills shown';
      el.hidden = false;
    }

    applyZeroMatchWarning(optionValues) {
      if (!this.isDevSurface()) {
        this.hidden = true;
        return;
      }
      const el = this.querySelector('[data-sf-devwarn]');
      if (!el) return;
      const values = optionValues.length ? optionValues.join(', ') : '(no size option values)';
      el.textContent = `NO CHART ROWS MATCH THIS PRODUCT'S SIZES — "${this.chartName || 'size chart'}" has no row for: ${values}`;
      el.hidden = false;
    }

    hydrateAgeChips() {
      // The server-rendered chips are only a pre-hydration skeleton — always
      // rebuild from the parsed JSON so the chips can never drift from the
      // data driving the model (a same-count label edit used to slip through)
      this.sfAges.innerHTML = '';
      this.bands.forEach((a) => {
        const b = document.createElement('button');
        b.className = 'sf-chip';
        b.type = 'button';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', 'false');
        b.setAttribute('aria-label', `${a.label} ${a.unit}`);
        b.innerHTML = `<span class="sf-cv">${a.label}</span><span class="sf-cu">${a.unit}</span>`;
        this.sfAges.appendChild(b);
      });
      [...this.sfAges.children].forEach((b, i) => {
        b.addEventListener('click', () => {
          /* An age is ALWAYS selected — a pill click never deselects. Cream vs
             taupe is the only thing that toggles:
             - same pill  → flip engaged (collapse the cards to cream, or expand
               them to taupe). bandI is unchanged.
             - other pill → switch to it and expand (taupe); switching age keeps
               you "in" the finder. */
          if (this.bandI === i) {
            // same pill: only the reveal toggles (cream <-> taupe). A size
            // already confirmed for this band (e.g. seeded on load from an
            // already-checked variant) must survive opening the panel — the
            // shopper isn't re-picking, just looking.
            this.engaged = !this.engaged;
          } else {
            this.bandI = i;
            this.engaged = true;
            this.selection.chooseRecommendation(this.recommendIdx());
          }
          this.renderFinder();
        });
      });
    }

    /* The recommended size index for the current band: always the split winner,
       even when sold out. This keeps the finder honest: the most common size is
       still the recommendation, and the ATC should show sold out rather than
       silently selecting a neighbouring in-stock variant. */
    recommendIdx() {
      if (this.bandI === null) return null;
      const band = this.bands[this.bandI];
      const split = this.direct ? this.directSplit() : this.bracketSplit(band.lo, band.hi);
      return split.indexOf(Math.max(...split));
    }

    /* Build the size-chart table from the finder's own data and drop it into the
       modal — once, at init (the data doesn't change). One column per size row
       plus a column per measurement dimension, so height/chest/hip are stated as
       headers rather than repeated per cell. Columns are the union of dimensions
       across all sizes, in first-seen order; headers stay lowercase as the data
       has them. Empty/malformed rows are simply skipped. */
    /* build the size-chart modal table once, from the shared builder (the same
       one the size-chart page uses). this.sizes carries the primary measure as
       `cm`; map it to the builder's {size, primary, secondary} shape. */
    buildChartTable() {
      buildChartTable(this.querySelector('[data-sf-chart]'),
        this.sizes.map((s) => ({ size: s.size, primary: s.cm, secondary: s.secondary })));
    }

    /* highlight the size-chart row matching the finder's current selection, so
       the table and the size cards above stay connected. Called on every render
       (the selection changes); the table itself is built once. */
    markChartRow() {
      markChartRow(this.querySelector('[data-sf-chart]'), this.selection.currentIdx());
    }

    /* stamp the selected size on the ATC button ("Add to Bag · S"); called on
       every render AND from the MutationObserver, because the theme's variant
       flow replaces the button after each variant change. Write-if-different
       so the observer can't loop; a disabled button keeps the theme's own
       sold-out wording */
    applyAtcLabel() {
      const btn = this.productForm?.querySelector('.add-to-cart');
      if (!btn) return;
      const idx = this.selection.currentIdx();

      /* an explicitly picked sold-out size disables the CTA with the theme's
         own no_stock wording — this also covers sizes with NO matching
         variant at all (e.g. Premmie missing on a product), which the
         theme's variant flow can never disable by itself. The data flag
         marks "we disabled this" so an in-stock re-pick can re-enable
         immediately instead of waiting for the variant fetch */
      if (idx != null && !this.inStock(idx)) {
        btn.disabled = true;
        btn.dataset.sfSoldout = '1';
        if (btn.textContent.trim() !== this.soldOutText) btn.textContent = this.soldOutText;
        return;
      }
      /* idx == null means the finder has no usable recommendation at all. Fall
         back to the theme's own sold-out wording, genuinely disabled. */
      if (idx == null) {
        btn.disabled = true;
        btn.dataset.sfSoldout = '1';
        if (btn.textContent.trim() !== this.soldOutText) btn.textContent = this.soldOutText;
        return;
      }
      if (btn.dataset.sfSoldout) {
        delete btn.dataset.sfSoldout;
        btn.disabled = false;
      }
      if (btn.disabled) return; // the theme's own sold-out state wins
      const base = btn.dataset.addToCartText || 'Add to Bag';
      const size = this.sizes[idx].size;
      /* "Size" must precede the value: a bare size code of "0" or "00" reads as
         a dollar amount or quantity ("Add to Bag · 0" looks like $0). For the
         default bag CTA, weave it in — "Add Size 0 to Bag". For any other base
         (e.g. a localized "Pre-order"), the "Add … to Bag" shape doesn't fit, so
         suffix it while still leading with "Size" — "Pre-order · Size 0". */
      const label = /^Add to Bag$/i.test(base)
        ? `Add Size ${size} to Bag`
        : `${base} · Size ${size}`;
      if (btn.textContent.trim() !== label) btn.textContent = label;
    }

    renderFinder() {
      [...this.sfAges.children].forEach((b, i) => {
        b.setAttribute('aria-checked', i === this.bandI ? 'true' : 'false');
      });

      /* An age is ALWAYS selected (bandI is never null), so the ATC always has a
         size to stand behind — there is no "select an age" dead state. The only
         gate is `revealed` = engaged: whether the shopper has expanded the size
         cards. Collapsed → the selected pill shows CREAM (sf-ages--default) and
         the cards/summary/tip stay hidden while the ATC still pre-fills the
         recommendation. Expanded → taupe pill, cards shown. */
      const revealed = this.engaged;

      this.sfAges.classList.toggle('sf-ages--default', !revealed);
      this.sfSizes.hidden = !revealed;
      // the "Measure for best fit" toggle only makes sense once the cards show
      if (this.sfHowRow) this.sfHowRow.hidden = !revealed;
      if (!revealed) this.closeTip();
      if (!revealed) {
        // collapsed: pre-fill the ATC from the selected band's recommendation,
        // but skip the reveal (cards hidden)
        this.selection.setRecommendation(this.recommendIdx());
        this.applyAtcLabel();
        this.markChartRow();
        this.updateSwatchLinks();
        return;
      }

      const band = this.bands[this.bandI];
      const split = this.direct ? this.directSplit() : this.bracketSplit(band.lo, band.hi);
      const mostIdx = split.indexOf(Math.max(...split));
      /* Three fixed columns, in DOM = visual order (the grid no longer places
         cards explicitly). An out-of-range neighbour at the smallest/largest
         size keeps its slot as a struck-through empty card, so the row always
         reads as three cards and the hero stays centred.

         Only the hero shows a visible label ("Most common"). The neighbours
         carry none — their position (left = smaller, right = larger) says it,
         and the size codes are ordered. `announce` still gives assistive tech
         that context via the aria-label, since a SR user has no position cue. */
      const slots = [
        { i: mostIdx - 1, kind: 'down', label: '', announce: 'size down' },
        { i: mostIdx, kind: 'primary', label: 'Most common', announce: `most common for ${band.label} ${band.unit}` },
        { i: mostIdx + 1, kind: 'up', label: '', announce: 'size up' },
      ].map((c) => (c.i >= 0 && c.i < this.sizes.length ? { ...c, share: split[c.i] } : null));
      /* The hint (and variant sync / ATC) stands behind the recommendation: the
         split winner, even when sold out. The sold-out CTA is deliberate because
         choosing a neighbouring size by default is less honest than showing the
         shopper that their most common size is unavailable. */
      const hintIdx = this.recommendIdx();
      this.selection.setRecommendation(hintIdx);

      // rebuilding the cards nukes keyboard focus — remember the slot and restore
      const focusSlot = [...this.sfSizes.children].indexOf(document.activeElement);

      this.sfSizes.innerHTML = '';
      slots.forEach((c) => {
        if (!c) {
          /* edge of the size ladder (no smaller/larger neighbour): a struck-
             through empty card (shared emptyCardInnerHtml, sized against the
             hero's measure), so the row still reads as three cards.
             Non-interactive, hidden from AT. */
          const empty = document.createElement('div');
          empty.className = 'sf-size sf-size--empty';
          empty.setAttribute('aria-hidden', 'true');
          empty.innerHTML = emptyCardInnerHtml(this.sizes[mostIdx].cm);
          this.sfSizes.appendChild(empty);
          return;
        }
        const s = this.sizes[c.i];
        const stocked = this.inStock(c.i);
        /* a card shows the ✓ only once the shopper has CONFIRMED a size (clicked
           a card) for this age. Before that — e.g. straight after an age change —
           no card is ticked, even though the middle card keeps its cream hint
           and the ATC pre-fills the recommendation. `confirmed` resets on every
           age change (see the chip handler and syncFromVariant). */
        const isSel = this.selection.confirmed && c.i === this.selection.currentIdx();
        const b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('role', 'radio');
        b.className = `sf-size sf-size--${c.kind}${stocked ? '' : ' oos'}`;
        b.setAttribute('aria-checked', isSel ? 'true' : 'false');
        /* every card carries the PRIMARY measure only — the three narrow
           columns can't hold a clothing row's full set ("height 76 cm, chest
           50 cm, hip 58 cm" wrapped mid-unit). The full per-dimension breakdown
           lives in the size-chart modal (buildChartTable). The aria-label still
           announces the complete measure for assistive tech. */
        const full = s.secondary ? `${s.cm}, ${s.secondary}` : s.cm;
        b.setAttribute('aria-label', `Size ${s.size}, ${full}, ${c.announce || c.label}${stocked ? '' : ', sold out'}`);
        /* two dedicated banner rows, so the recommendation and availability
           never share a strip: TOP = "Most common" (hero only), BOTTOM =
           "Sold out" (any out-of-stock card). Either may be absent. */
        const topBanner = c.kind === 'primary'
          ? '<span class="sf-banner sf-banner--top">Most common</span>' : '';
        const botBanner = !stocked
          ? '<span class="sf-banner sf-banner--bottom">Sold out</span>' : '';
        /* card inner from the SHARED cardInnerHtml ("size" label over the
           code, primary measure descriptor-over-value) — the same markup the
           chart page renders, so the tiles can't drift. */
        b.innerHTML = `${topBanner}${cardInnerHtml(s.size, s.cm)}${botBanner}`;
        b.addEventListener('click', () => {
          // toggle: clicking the already-ticked card deselects it, dropping back
          // to the hinted-but-unticked state (recommendation still stands behind
          // the ATC, no ✓). Clicking any other card selects it.
          this.selection.toggleExplicit(c.i);
          this.renderFinder();
        });
        this.sfSizes.appendChild(b);
      });

      if (focusSlot > -1) {
        const fresh = this.sfSizes.children[focusSlot];
        if (fresh && fresh.tagName === 'BUTTON') fresh.focus();
      }

      // keep the real variant (hidden id input, price, ATC state) in sync with
      // whatever the finder currently recommends
      const targetIdx = this.selection.currentIdx();
      if (targetIdx != null) this.selectVariant(targetIdx);
      this.applyAtcLabel();
      this.markChartRow();
      this.updateSwatchLinks();
    }

    /* ---- keyboard + tip -------------------------------------------------- */

    bindKeyboard() {
      /* arrow keys move between age bands (radiogroup pattern);
         before any pick, arrows start from the focused chip */
      this.sfAges.addEventListener('keydown', (e) => {
        const MAXB = this.bands.length - 1;
        const cur = this.bandI !== null ? this.bandI : Math.max(0, [...this.sfAges.children].indexOf(document.activeElement));
        const go = (i) => {
          this.bandI = i;
          this.engaged = true; // arrowing onto an age engages, like a click
          this.selection.chooseRecommendation(this.recommendIdx());
          this.renderFinder();
          this.sfAges.children[this.bandI].focus();
          e.preventDefault();
        };
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') go(Math.min(MAXB, cur + 1));
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') go(Math.max(0, cur - 1));
        if (e.key === 'Home') go(0);
        if (e.key === 'End') go(MAXB);
      });

      /* same pattern on the size tiles: arrows select the neighbouring real
         size (skipping the dashed edge placeholder) */
      this.sfSizes.addEventListener('keydown', (e) => {
        let d = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') d = 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') d = -1;
        else return;
        const tiles = [...this.sfSizes.children];
        const cur = tiles.indexOf(document.activeElement);
        if (cur < 0) return;
        e.preventDefault();
        let n = cur + d;
        while (n >= 0 && n < tiles.length && tiles[n].tagName !== 'BUTTON') n += d;
        if (n < 0 || n >= tiles.length) return;
        tiles[n].click(); // click re-renders the row…
        const fresh = this.sfSizes.children[n]; // …so focus the freshly built tile
        if (fresh && fresh.tagName === 'BUTTON') fresh.focus();
      });
    }

    /* collapse the measuring-tip / size-chart card — used by Escape and when the
       finder hides the whole toggle row on an age toggle-off. No-op if the tip
       elements aren't present or it's already closed. */
    closeTip() {
      if (!this.tipPop || this.tipPop.hidden) return;
      this.tipPop.hidden = true;
      this.tipBtn?.setAttribute('aria-expanded', 'false');
    }

    /* switch the tip card's tabs (Chart / How to measure) — the SHARED
       selectTab, keyed by data-sf-tab ↔ data-sf-panel (same as the chart
       page). */
    selectTab(name) {
      selectTab(this, name);
    }

    bindTip() {
      if (!this.tipBtn || !this.tipPop) return;
      // the entry link is "Size chart", so opening lands on the Chart tab
      this.tipBtn.addEventListener('click', () => {
        const open = this.tipPop.hidden;
        this.tipPop.hidden = !open;
        this.tipBtn.setAttribute('aria-expanded', String(open));
        if (open) this.selectTab('chart');
      });
      this.querySelectorAll('[data-sf-tab]').forEach((t) => {
        t.addEventListener('click', () => this.selectTab(t.dataset.sfTab));
      });
      // Escape closes the card when focus is inside it, and returns focus to the
      // "Size chart" trigger — there's no in-card X, so this is the keyboard exit
      this.tipPop.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || this.tipPop.hidden) return;
        this.closeTip();
        this.tipBtn.focus();
      });
    }
  }

  if (!window.customElements.get('size-finder')) {
    window.customElements.define('size-finder', SizeFinder);
  }
})();
